import type { ParticleMaterialProfile } from './profiles';
import {
  clampEnergy,
  energyFineCountScale,
  energyLaunch,
  energyRadius,
  mixEnergyCone,
} from '../../services/energy';
import {
  impactFineCountScale,
  impactHazeScale,
  impactLingerScale,
  impactSpeedScale,
  impactSplashScale,
  impactSpreadScale,
  impactStampScale,
  impactStoredUpFactor,
  mixImpactCone,
  resolveImpact,
  type ImpactParams,
} from './impactRegime';
import { mulberry32, seedFromBurst } from './spawn';
import {
  DEFAULT_SURFACE_NORMAL,
  WALL_SPLASH_FRACTION,
  coneAround,
  deriveImpactFrame,
  normalize3,
} from './impactFrame';

export interface CloudSpawnRecord {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly vx: number;
  readonly vy: number;
  readonly vz: number;
  readonly size: number;
  readonly seed: number;
  readonly kind: number;
  readonly depositMass: number;
  readonly ox: number;
  readonly oy: number;
  readonly oz: number;
  readonly energy: number;
  readonly r: number;
  readonly g: number;
  readonly b: number;
  readonly linger: number;
  readonly buoyancy: number;
  readonly nx: number;
  readonly ny: number;
  readonly nz: number;
  readonly upFactor: number;
}

export interface CloudPlanInput {
  readonly position: readonly [number, number, number];
  readonly direction: readonly [number, number, number];
  readonly energy: number;
  readonly fineMass: number;
  readonly profile: ParticleMaterialProfile;
  readonly burstIndex: number;
  readonly maxCount?: number;
  readonly surfaceNormal?: readonly [number, number, number];
  readonly impact?: ImpactParams;
}

/** Sprites per kg of *fine* mass. Fewer larger puffs beat a disc field. */
function spriteDensity(id: ParticleMaterialProfile['id']): number {
  if (id === 'brick') return 2200;
  if (id === 'concrete') return 2000;
  if (id === 'plaster') return 2600;
  if (id === 'wood') return 700;
  if (id === 'glass') return 220;
  return 1000;
}

function cloudSizeRange(
  id: ParticleMaterialProfile['id'],
  volume: boolean,
): readonly [number, number] {
  if (volume) {
    if (id === 'brick') return [0.34, 0.58];
    if (id === 'concrete') return [0.32, 0.54];
    if (id === 'plaster') return [0.30, 0.52];
    if (id === 'wood') return [0.22, 0.40];
    if (id === 'glass') return [0.12, 0.22];
    return [0.36, 0.60];
  }
  if (id === 'brick') return [0.10, 0.24];
  if (id === 'concrete') return [0.10, 0.22];
  if (id === 'plaster') return [0.08, 0.20];
  if (id === 'wood') return [0.08, 0.18];
  if (id === 'glass') return [0.05, 0.12];
  return [0.12, 0.26];
}

export function plannedFineCount(
  fineMass: number,
  profile: ParticleMaterialProfile,
  energy: number,
  cap = 1e9,
  impact?: ImpactParams,
): number {
  const scale = energyFineCountScale(energy);
  const raw = Math.round(Math.max(0, fineMass) * spriteDensity(profile.id) * scale);
  const scaled = Math.round(raw * impactFineCountScale(resolveImpact(impact)));
  return Math.max(0, Math.min(scaled, Math.max(0, cap)));
}



function varyWithinFamily(
  base: readonly [number, number, number],
  rng: () => number,
  amp: number,
): [number, number, number] {
  const j = amp * (rng() * 2 - 1);
  const clamp = (x: number) => Math.min(1, Math.max(0, x));
  return [clamp(base[0] + j), clamp(base[1] + j * 0.5), clamp(base[2] + j * 0.3)];
}

function pickKindAndTint(
  rng: () => number,
  profile: ParticleMaterialProfile,
  family: 'crater' | 'mortar' | 'legacy',
): { kind: number; tint: readonly [number, number, number] } {
  const tints = profile.cloudTints;
  if (profile.signature.sourceTintByFamily && tints.length >= 2) {
    if (family === 'crater') {
      return { kind: 0, tint: varyWithinFamily(tints[0], rng, 0.035) };
    }
    return { kind: 1, tint: varyWithinFamily(tints[1], rng, 0.028) };
  }
  if (profile.id === 'brick' && tints.length >= 2) {
    if (rng() < 0.6) return { kind: 0, tint: tints[0] };
    return { kind: 1, tint: tints[1] };
  }
  if (profile.id === 'concrete') {
    const tint = tints.length >= 2 && rng() >= 0.55 ? tints[1] : tints[0];
    return { kind: 2, tint };
  }
  return { kind: 3, tint: tints[0] };
}

function wallAperture(profile: ParticleMaterialProfile, energy: number): number {
  const cone = profile.signature.ejectionConeRad;
  if (cone) return mixEnergyCone(cone[0], cone[1], energy);
  return 0.32 + energyLaunch(energy) * 0.20;
}

/**
 * Pure fines planner. Seed is a hash of the burst counter (salted so the
 * cloud stream is independent of the grain stream). No wall-clock, no
 * Math.random.
 */
export function planCloudBurst(input: CloudPlanInput): CloudSpawnRecord[] {
  const { profile } = input;
  const imp = resolveImpact(input.impact);
  const count = plannedFineCount(
    input.fineMass,
    profile,
    input.energy,
    input.maxCount ?? 1e9,
    input.impact,
  );
  if (count === 0) return [];
  const rng = mulberry32(seedFromBurst(input.burstIndex) ^ 0x51edc10d);
  const origin = input.position;
  const dir = normalize3(input.direction);
  const frame = deriveImpactFrame(input.surfaceNormal ?? DEFAULT_SURFACE_NORMAL);
  const energy = energyLaunch(input.energy);
  const storedEnergy = clampEnergy(input.energy);
  const rScale = energyRadius(input.energy) * impactStampScale(imp);
  const speedMul = impactSpeedScale(imp);
  const lingerMul = impactLingerScale(imp);
  const splashMul = impactSplashScale(imp);
  const hazeMul = impactHazeScale(imp);
  const storedUp = impactStoredUpFactor(frame.upFactor, imp);
  const speed0 = 0.9 + energy * 2.35;
  const spread0 = 0.38 + energy * 0.16;
  const spread = spread0 * impactSpreadScale(imp);
  const depositEach = input.fineMass / count;
  const records: CloudSpawnRecord[] = new Array(count);
  const blendWall = frame.upFactor < 0.999;
  const sig = profile.signature;
  const familyMode = sig.sourceTintByFamily;

  for (let i = 0; i < count; i += 1) {
    const crater = familyMode ? rng() < sig.craterFamilyFrac : true;
    const family: 'crater' | 'mortar' | 'legacy' = familyMode
      ? (crater ? 'crater' : 'mortar')
      : 'legacy';
    const core = crater && rng() < 0.38;
    const jitter = (core ? 0.07 + 0.05 * rng() : 0.18 + 0.22 * energy * rng()) * rScale;
    let px = origin[0] + (rng() * 2 - 1) * jitter;
    let py = origin[1] + (rng() * 2 - 1) * jitter * 0.35 + (core ? 0.06 : 0.02);
    let pz = origin[2] + (rng() * 2 - 1) * jitter;
    const velDir = coneAround(rng, dir, spread);
    const speedScale = family === 'mortar' ? sig.mortarSpeedScale : 1;
    const speed = speed0 * (0.5 + rng() * 0.72) * speedScale * speedMul;
    let vx = velDir[0] * speed;
    let vy = velDir[1] * speed;
    let vz = velDir[2] * speed;
    if (core) {
      const lift = (1.15 + 1.7 * energy) * (0.7 + 0.3 * rng()) * (profile.cloudBuoyancy / 2.6);
      vy += lift;
    }
    // Extra radial kick so the later ground current has an annulus to ride.
    const rx = px - origin[0];
    const rz = pz - origin[2];
    const rLen = Math.hypot(rx, rz);
    if (rLen > 1e-5) {
      const kick = (0.15 + 0.35 * energy) * (0.4 + rng()) * speedScale;
      vx += (rx / rLen) * kick;
      vz += (rz / rLen) * kick;
    }
    if (blendWall) {
      const upBias = core ? 0.06 : 0.02;
      const k = 1 - frame.upFactor;
      py -= upBias * k;
      px += frame.normal[0] * upBias * k;
      py += frame.normal[1] * upBias * k;
      pz += frame.normal[2] * upBias * k;
      const aperture = mixImpactCone(wallAperture(profile, energy), imp);
      const splashFrac = WALL_SPLASH_FRACTION * (1 - frame.upFactor) * splashMul;
      const mortarSplash = family === 'mortar';
      let wvx: number;
      let wvy: number;
      let wvz: number;
      if (mortarSplash || rng() < splashFrac) {
        const phi = rng() * Math.PI * 2;
        const splash = (0.55 + 0.9 * energy) * (0.5 + rng() * 0.7) * speedScale * speedMul;
        const cp = Math.cos(phi);
        const sp = Math.sin(phi);
        wvx = (frame.tangent[0] * cp + frame.bitangent[0] * sp) * splash;
        wvy = (frame.tangent[1] * cp + frame.bitangent[1] * sp) * splash;
        wvz = (frame.tangent[2] * cp + frame.bitangent[2] * sp) * splash;
      } else {
        const wDir = coneAround(rng, frame.normal, aperture);
        const wSpeed = speed0 * (0.55 + rng() * 0.7) * speedMul;
        wvx = wDir[0] * wSpeed;
        wvy = wDir[1] * wSpeed;
        wvz = wDir[2] * wSpeed;
      }
      const u = frame.upFactor;
      vx = wvx + (vx - wvx) * u;
      vy = wvy + (vy - wvy) * u;
      vz = wvz + (vz - wvz) * u;
    }
    if (imp.blastFraction > 0) {
      vy += (1.15 + 1.7 * energy) * imp.blastFraction * 0.85;
    }
    const { kind, tint } = pickKindAndTint(rng, profile, family);
    const haze = sig.hazeFraction > 0 && rng() < sig.hazeFraction * hazeMul;
    const lingerBase = haze && sig.hazeLinger > 0 ? sig.hazeLinger : profile.cloudLinger;
    const linger = lingerBase * (0.82 + rng() * 0.32) * lingerMul;
    // ~25% large volume sprites + ~75% smaller wisps. Clay still biases
    // toward the high end of whichever class it rolled.
    const volume = rng() < 0.25;
    const [s0, s1] = cloudSizeRange(profile.id, volume);
    const u = rng();
    const sizeT = kind === 0 ? Math.sqrt(u) : u;
    records[i] = {
      x: px,
      y: py,
      z: pz,
      vx,
      vy,
      vz,
      size: (s0 + (s1 - s0) * sizeT) * rScale,
      seed: rng(),
      kind,
      depositMass: depositEach,
      ox: origin[0],
      oy: origin[1],
      oz: origin[2],
      energy: storedEnergy,
      r: tint[0],
      g: tint[1],
      b: tint[2],
      linger,
      buoyancy: profile.cloudBuoyancy,
      nx: frame.normal[0],
      ny: frame.normal[1],
      nz: frame.normal[2],
      upFactor: storedUp,
    };
  }
  return records;
}
